import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AdminProduct } from '../model/admin-product';
import { HierarchyItem } from '../model/hierarchy-item';
import { Order } from '../model/order';
import { Page } from '../model/page';
import { ProductItem } from '../model/product-item';
import { ApiService } from './api.service';

/**
 * The shop owner's side of the storefront.
 *
 * Whether the visitor is an administrator is decided by unimarket-api from the admin cookie and
 * arrives on every page; the UI only follows that flag. Every endpoint below re-checks it, so
 * hiding a button is a courtesy rather than the protection.
 */
@Injectable({ providedIn: 'root' })
export class AdminService {

  private api = inject(ApiService);

  readonly isAdmin = signal(false);

  product(id: number): Promise<AdminProduct> {
    return firstValueFrom(this.api.get<AdminProduct>(`/admin/products/${id}`));
  }

  edit(id: number, product: Partial<ProductItem>): Promise<ProductItem> {
    return firstValueFrom(this.api.patch<ProductItem>(`/admin/products/${id}`, product));
  }

  changeStatus(id: number, status: string): Promise<ProductItem> {
    return firstValueFrom(this.api.post<ProductItem>(`/admin/products/${id}/status/${status}`));
  }

  addPhotos(id: number, files: FileList): Promise<ProductItem> {
    const form = new FormData();
    Array.from(files).forEach(file => form.append('files', file));
    return firstValueFrom(this.api.post<ProductItem>(`/admin/products/${id}/photos`, form));
  }

  addPhotoUrl(id: number, url: string): Promise<ProductItem> {
    return firstValueFrom(this.api.post<ProductItem>(`/admin/products/${id}/photos/url?url=${encode(url)}`));
  }

  deletePhoto(id: number, url: string): Promise<ProductItem> {
    return firstValueFrom(this.api.delete<ProductItem>(`/admin/products/${id}/photos?url=${encode(url)}`));
  }

  movePhoto(id: number, url: string, direction: 'up' | 'down'): Promise<ProductItem> {
    return firstValueFrom(this.api.post<ProductItem>(
      `/admin/products/${id}/photos/move?url=${encode(url)}&direction=${direction}`));
  }

  addColor(id: number, value: string): Promise<ProductItem> {
    return firstValueFrom(this.api.post<ProductItem>(`/admin/products/${id}/colors?value=${encode(value)}`));
  }

  addSize(id: number, value: string): Promise<ProductItem> {
    return firstValueFrom(this.api.post<ProductItem>(`/admin/products/${id}/sizes?value=${encode(value)}`));
  }

  createProduct(categoryCode: string): Promise<ProductItem> {
    return firstValueFrom(this.api.post<ProductItem>(`/admin/categories/${categoryCode}/products`));
  }

  orders(): Promise<Order[]> {
    return firstValueFrom(this.api.get<Order[]>('/admin/orders'));
  }

  order(code: string): Promise<Order> {
    return firstValueFrom(this.api.get<Order>(`/admin/orders/${code}`));
  }

  changeOrderStatus(code: string, status: string): Promise<Order> {
    return firstValueFrom(this.api.post<Order>(`/admin/orders/${code}/${status}`));
  }

  hierarchy(page: number, size: number): Promise<Page<HierarchyItem>> {
    return firstValueFrom(this.api.get<Page<HierarchyItem>>(
      `/admin/hierarchy?page=${page}&pageSize=${size}`));
  }

}

/** Only the API's own refusals are translated: Spring's error bodies, which also carry a path, are in English. */
export function adminFailure(error: unknown, otherwise: string): string {
  const response = error as { status?: number; error?: { message?: string; path?: string } } | null;
  if (response?.status === 403) {
    return 'Це може зробити лише власник магазину. Увійдіть як адміністратор.';
  }
  if (response?.status === 400 && response.error?.message && !response.error.path) {
    return response.error.message;
  }
  return otherwise;
}

function encode(value: string) {
  return encodeURIComponent(value);
}
